/**
 * Validation Utilities
 *
 * Input validation functions for file uploads, credit checks, and storage quotas.
 *
 * CRITICAL CONSTRAINTS:
 * - TypeScript strict mode (NO 'any' types)
 * - User-friendly error messages
 * - Match backend validation rules
 */

/**
 * Validate image file for upload
 *
 * @param file - File to validate
 * @returns Error message if invalid, null if valid
 */
export function validateImageFile(file: File): string | null {
  // Check file type
  if (!file.type.startsWith('image/')) {
    return 'Only image files are allowed';
  }

  // Check file size (10MB max)
  const maxSize = 10 * 1024 * 1024; // 10MB in bytes
  if (file.size > maxSize) {
    return 'File size must be under 10MB';
  }

  // Check for valid image extensions
  const validExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];
  const fileName = file.name.toLowerCase();
  const hasValidExtension = validExtensions.some((ext) => fileName.endsWith(ext));

  if (!hasValidExtension) {
    return 'File must be JPG, PNG, WEBP, or GIF format';
  }

  return null;
}

/**
 * Validate storage quota
 *
 * @param used - Current storage used in bytes
 * @param limit - Storage limit in bytes
 * @param additionalSize - Size of file to upload in bytes
 * @returns Error message if would exceed limit, null if valid
 */
export function validateStorageQuota(
  used: number,
  limit: number,
  additionalSize: number
): string | null {
  const afterUpload = used + additionalSize;

  if (afterUpload > limit) {
    const usedMB = Math.round(used / (1024 * 1024));
    const limitMB = Math.round(limit / (1024 * 1024));
    return `Storage limit exceeded. You're using ${usedMB}MB of ${limitMB}MB. Please delete some files or contact support.`;
  }

  return null;
}

/**
 * Validate user has sufficient credits
 *
 * @param available - Credits available to user
 * @param required - Credits required for service
 * @returns Error message if insufficient, null if valid
 */
export function validateCredits(available: number, required: number): string | null {
  if (available < required) {
    return `Insufficient credits. You need ${required} credits but only have ${available}. Please purchase more credits.`;
  }

  return null;
}

/**
 * Validate description text input
 *
 * @param text - Text to validate
 * @param maxLength - Maximum allowed length
 * @returns Error message if invalid, null if valid
 */
export function validateDescriptionText(text: string, maxLength: number = 999): string | null {
  if (!text || text.trim().length === 0) {
    return 'Please provide property details';
  }

  if (text.length > maxLength) {
    return `Description must be under ${maxLength} characters (currently ${text.length})`;
  }

  return null;
}

/**
 * Validate removal prompt text
 *
 * @param text - Prompt text to validate
 * @returns Error message if invalid, null if valid
 */
export function validateRemovalPrompt(text: string): string | null {
  if (!text || text.trim().length === 0) {
    return 'Please describe what you want to remove';
  }

  if (text.length < 5) {
    return 'Please provide more details (e.g., "remove the red car in the driveway")';
  }

  return null;
}
